import { GfxControllerIdentifier } from '@blocksuite/affine/std/gfx';
import { useEffect, useRef, useState } from 'react';
import * as Y from 'yjs';

import type { AffineEditorContainer } from './blocksuite-editor';
import * as styles from './object-annotation.css';
import {
  ANNOTATION_KEY,
  type ObjectAnnotation,
} from './object-annotation-store';

interface CanvasAnnotation {
  id: string;
  status: string;
  tags: string[];
  labels: [string, string][];
  height: number;
  armed: boolean;
  x: number;
  y: number;
  width: number;
  scale: number;
  compact: boolean;
  showStatus: boolean;
  frameName: string;
  quiet: boolean;
}

/** Read-only display; metadata stays in each native object, not in this overlay. */
export function ObjectAnnotationCanvas({
  editor,
}: {
  editor: AffineEditorContainer;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const [items, setItems] = useState<CanvasAnnotation[]>([]);
  useEffect(() => {
    let cancelled = false;
    let frame = 0;
    let dispose = () => {};
    editor.updateComplete
      .then(() => {
        if (cancelled) return;
        const gfx = editor.std?.getOptional(GfxControllerIdentifier);
        const container = holder.current;
        if (!gfx || !container) return;
        const update = () => {
          frame = 0;
          if (cancelled) return;
          const viewport = gfx.viewport;
          const scale = viewport.zoom * viewport.viewScale;
          const rect = container.getBoundingClientRect();
          const models = [
            ...(gfx.surface?.elementModels ?? []),
            ...editor.doc.getModelsByFlavour([
              'affine:note',
              'affine:frame',
              'affine:edgeless-text',
              'affine:image',
            ]),
          ];
          const next: CanvasAnnotation[] = [];
          for (const model of models) {
            if (!('elementBound' in model)) continue;
            let bound = model.elementBound as {
              x: number;
              y: number;
              w: number;
              h: number;
            };
            const isConnector = 'type' in model && model.type === 'connector';
            const labelRect =
              'type' in model &&
              model.type === 'connector' &&
              'labelXYWH' in model &&
              Array.isArray(model.labelXYWH)
                ? model.labelXYWH
                : null;
            const compact =
              labelRect?.length === 4 && labelRect.every(Number.isFinite);
            if (compact && labelRect) {
              bound = {
                x: labelRect[0],
                y: labelRect[1],
                w: labelRect[2],
                h: labelRect[3],
              };
            }
            if (![bound.x, bound.y, bound.w, bound.h].every(Number.isFinite))
              continue;
            const data = 'yMap' in model ? model.yMap : model.yBlock;
            if (!(data instanceof Y.Map)) continue;
            const nativeFrameName =
              'flavour' in model && model.flavour === 'affine:frame'
                ? String(data.get('prop:title') ?? '')
                : '';
            const value = data.get(ANNOTATION_KEY);
            const annotation = (
              value instanceof Y.Map ? value.toJSON() : value
            ) as ObjectAnnotation | undefined;
            const status = annotation?.status ?? 'none';
            const quiet = status === 'none';
            const showStatus = !quiet && !isConnector;
            const frameName = quiet ? '' : nativeFrameName;
            const tags = Object.entries(quiet ? {} : (annotation?.tags ?? {}))
              .filter(([, active]) => active)
              .map(([name]) => '#' + name);
            const labels = Object.entries(
              quiet ? {} : (annotation?.labels ?? {})
            );
            const armed = annotation?.aiArmed === true;
            if (!showStatus && !armed && !tags.length && !labels.length)
              continue;
            const height =
              Math.max(
                armed ? (showStatus ? 2 : 1) : showStatus ? 1 : 0,
                tags.length,
                labels.length
              ) *
                24 +
              4;
            const width =
              (quiet || compact) && !tags.length && !labels.length
                ? Math.max(bound.w, 64)
                : Math.max(bound.w, 260);
            const [left, top] = viewport.toViewCoord(
              bound.x + (bound.w - width) / 2,
              bound.y
            );
            const x = left + viewport.left - rect.left;
            const y = top + viewport.top - rect.top - (height + 4) * scale;
            if (
              x + width * scale < 0 ||
              x > rect.width ||
              y + height * scale < 0 ||
              y > rect.height
            )
              continue;
            next.push({
              id: model.id,
              status,
              quiet,
              tags,
              labels,
              height,
              armed,
              x,
              y,
              width,
              scale,
              compact: !!compact,
              showStatus,
              frameName,
            });
          }
          setItems(next);
        };
        const schedule = () => {
          if (!cancelled && !frame) frame = requestAnimationFrame(update);
        };
        const viewport = gfx.viewport.viewportUpdated.subscribe(schedule);
        const added = gfx.surface?.elementAdded.subscribe(schedule);
        const changed = gfx.surface?.elementUpdated.subscribe(schedule);
        const removed = gfx.surface?.elementRemoved.subscribe(schedule);
        const blocks = editor.doc.slots.blockUpdated.subscribe(schedule);
        editor.doc.spaceDoc.on('afterTransaction', schedule);
        const resize = new ResizeObserver(schedule);
        resize.observe(container);
        schedule();
        dispose = () => {
          viewport.unsubscribe();
          added?.unsubscribe();
          changed?.unsubscribe();
          removed?.unsubscribe();
          blocks.unsubscribe();
          editor.doc.spaceDoc.off('afterTransaction', schedule);
          resize.disconnect();
        };
      })
      .catch(console.error);
    return () => {
      cancelled = true;
      cancelAnimationFrame(frame);
      dispose();
    };
  }, [editor]);
  return (
    <div
      ref={holder}
      className={styles.canvasAnnotations}
      data-testid="canvas-object-annotations"
    >
      {items.map(item => (
        <div
          key={item.id}
          className={styles.canvasAnnotationRow}
          data-testid="canvas-object-annotation"
          data-object-id={item.id}
          aria-label={[
            item.showStatus ? item.status : '',
            item.armed ? 'ARM' : '',
            item.frameName,
            ...item.labels.map(([name]) => name),
            ...item.tags,
          ]
            .filter(Boolean)
            .join(', ')}
          style={{
            left: item.x,
            top: item.y,
            width: item.width,
            height: item.height,
            background: !item.showStatus ? 'transparent' : undefined,
            gridTemplateColumns: item.quiet
              ? 'minmax(0, 1fr)'
              : item.frameName
                ? 'minmax(0, 1fr) minmax(0, 2fr) minmax(0, 1fr)'
                : undefined,
            transform: 'scale(' + item.scale + ')',
          }}
        >
          <div
            className={styles.canvasWorkflow}
            style={
              item.quiet
                ? { alignItems: 'center', textAlign: 'center' }
                : undefined
            }
          >
            {item.armed && (
              <strong
                className={styles.canvasArmed}
                style={item.quiet ? { textAlign: 'center' } : undefined}
                data-testid="canvas-object-armed"
              >
                ARM
              </strong>
            )}
            {item.showStatus && (
              <span
                className={styles.canvasStatus}
                data-testid="canvas-object-status"
              >
                {item.status}
              </span>
            )}
          </div>
          {item.frameName && (
            <strong
              className={styles.canvasFrameName}
              data-testid="canvas-frame-name"
              title={item.frameName}
            >
              {item.frameName}
            </strong>
          )}
          {!item.quiet && (
            <div
              className={styles.canvasVocabulary}
              style={{
                gridTemplateColumns:
                  item.labels.length && item.tags.length
                    ? 'minmax(0, 1fr) minmax(0, 1fr)'
                    : 'minmax(0, 1fr)',
              }}
            >
              {!!item.labels.length && (
                <div
                  className={styles.canvasLabelStack}
                  data-testid="canvas-object-labels"
                >
                  {item.labels.map(([name, color]) => (
                    <div
                      className={styles.canvasLabelEntry}
                      key={name}
                      data-testid="canvas-object-label"
                    >
                      <span
                        className={styles.canvasLabelDot}
                        style={{ backgroundColor: color }}
                      />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              )}
              {!!item.tags.length && (
                <div
                  className={styles.canvasTags}
                  data-testid="canvas-object-tags"
                >
                  {item.tags.map(name => (
                    <div
                      className={styles.canvasTagEntry}
                      key={name}
                      data-testid="canvas-object-tag"
                    >
                      {name}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
